/*
  Sachê em 3D — seção lcw-pdp-sabores (opção "Sachê em 3D").

  - Um modelo só (sache-3d.glb, exportado do Blender) e a estampa troca
    por sabor (sache-3d-<sabor>-cor/mr/normal.webp, do Substance Painter).
  - Gira acompanhando a rolagem da página, com uma flutuação leve.
  - Ao trocar de sabor, dá uma volta rápida e troca a estampa quando o
    sachê está de lado (a troca não aparece).
  - Só carrega quando a seção chega perto da tela e não desenha fora dela.
  - Se o 3D falhar, a foto do sachê continua no lugar (é o padrão da seção).

  Three.js 0.170 vem do jsDelivr (versão fixa), aprovado em out/2026.
*/
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js/+esm';
import { DRACOLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/DRACOLoader.js/+esm';
import { MeshSurfaceSampler } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/math/MeshSurfaceSampler.js/+esm';

const semMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const suave = (t) => 1 - Math.pow(1 - t, 3);

const montar = async (palco) => {
  if (palco.dataset.pronto) return;
  palco.dataset.pronto = '1';
  const secao = palco.closest('[data-sabores]');
  const sabores = JSON.parse(palco.dataset.texturas || '[]');
  if (!sabores.length) return;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  const canvas = renderer.domElement;
  canvas.className = 'lcw-sab__3d-canvas';
  canvas.setAttribute('aria-hidden', 'true');

  const cena = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  // Estúdio para os reflexos: fundo escuro com faixas de luz (softboxes).
  // Metal só "parece metal" quando reflete contraste; num ambiente
  // uniforme ele vira cinza chapado. As faixas correm pelo "30" no giro.
  const estudio = new THREE.Scene();
  estudio.background = new THREE.Color(0x3a3f46);
  const faixa = (w, h, x, y, z, ry, forca) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(forca, forca, forca), side: THREE.DoubleSide }));
    m.position.set(x, y, z); m.lookAt(0, 0, 0); estudio.add(m);
  };
  faixa(1.2, 6, -3.2, 0.5, 2.5, 0, 2.4);   // softbox alto à esquerda
  faixa(1.0, 5, 3.4, 0, 1.5, 0, 1.8);    // softbox à direita
  faixa(6, 0.8, 0, 3.6, 1, 0, 1.2);        // faixa de cima
  faixa(4, 2.5, 0, -0.5, -4, 0, 0.5);    // rebatedor atrás
  cena.environment = pmrem.fromScene(estudio, 0.02).texture;
  const luz = new THREE.DirectionalLight(0xffffff, 1.6);
  luz.position.set(2, 3, 4);
  // luz de recorte do outro lado: desenha o brilho nas bordas do metalizado
  const recorte = new THREE.DirectionalLight(0xffffff, 0.9);
  recorte.position.set(-3, 1, 2);
  cena.add(recorte);
  cena.add(luz, new THREE.AmbientLight(0xffffff, 0.35));

  const camera = new THREE.PerspectiveCamera(28, 1, 0.01, 10);
  camera.position.set(0, 0, 0.44);

  // texturas: cache por sabor
  const loaderTex = new THREE.TextureLoader();
  const cache = {};
  const carregarTex = (url, cor) => new Promise((ok, erro) => loaderTex.load(url, (t) => {
    t.flipY = false; // padrão do glTF
    if (cor) t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    ok(t);
  }, undefined, erro));
  // frente e verso têm texturas próprias (no arquivo original os dois lados
  // usam o mesmo espaço de mapeamento); o relevo é igual em todos os sabores
  const normais = Promise.all([carregarTex(palco.dataset.normalFrente, false), carregarTex(palco.dataset.normalVerso, false)]);
  const texturasDe = (i) => (cache[i] = cache[i] || Promise.all([
    carregarTex(sabores[i].frente.cor, true), carregarTex(sabores[i].frente.mr, false),
    carregarTex(sabores[i].verso.cor, true), carregarTex(sabores[i].verso.mr, false),
  ]));

  const draco = new DRACOLoader();
  draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
  const gltf = await new GLTFLoader().setDRACOLoader(draco).loadAsync(palco.dataset.modelo);

  const [nFrente, nVerso] = await normais;
  // envMapIntensity alto: o metalizado (o "30", a etiqueta de sódio) precisa
  // de reflexo forte para parecer metal, e não cinza chapado
  const frente = new THREE.MeshStandardMaterial({ metalness: 1, roughness: 1, normalMap: nFrente, envMapIntensity: 1.0 });
  const verso = new THREE.MeshStandardMaterial({ metalness: 1, roughness: 1, normalMap: nVerso, envMapIntensity: 1.0 });
  const aplicar = ([fCor, fMr, vCor, vMr]) => {
    Object.assign(frente, { map: fCor, metalnessMap: fMr, roughnessMap: fMr });
    Object.assign(verso, { map: vCor, metalnessMap: vMr, roughnessMap: vMr });
    frente.needsUpdate = true; verso.needsUpdate = true;
  };
  const sache = gltf.scene;
  // o modelo traz dois materiais com o nome do lado: "frente" e "verso"
  sache.traverse((o) => { if (o.isMesh) o.material = /verso/i.test(o.material?.name || '') ? verso : frente; });

  // o sachê vem deitado do Blender: fica em pé, centrado, com o lado largo para a câmera
  const caixa = new THREE.Box3().setFromObject(sache);
  const tam = caixa.getSize(new THREE.Vector3());
  // +90° (e não -90°): em pé, de cabeça para cima e com a frente para a câmera
  if (tam.z > tam.y) sache.rotation.x = Math.PI / 2;
  const piv = new THREE.Group();
  piv.add(sache);
  const c2 = new THREE.Box3().setFromObject(piv).getCenter(new THREE.Vector3());
  sache.position.sub(c2);
  // na diagonal, como em foto de embalagem; o giro acontece em torno do
  // próprio eixo inclinado (piv gira dentro do grupo inclinado)
  const inclinado = new THREE.Group();
  inclinado.rotation.z = 0; // em pé (a diagonal foi testada e saiu)
  inclinado.add(piv);
  cena.add(inclinado);

  // Gotículas de condensação: meias-gotas achatadas espalhadas pela
  // superfície, em material de água (transparente, com refração). São
  // filhas da malha do sachê, então giram e trocam de sabor junto.
  if (palco.dataset.gotas !== 'false') {
    let malhas = [];
    sache.traverse((o) => { if (o.isMesh) malhas.push(o); });
    const agua = new THREE.MeshPhysicalMaterial({
      color: 0xffffff, metalness: 0, roughness: 0.04, transmission: 1, thickness: 0.6,
      ior: 1.33, clearcoat: 1, clearcoatRoughness: 0.02, envMapIntensity: 1.6,
    });
    const gota = new THREE.SphereGeometry(1, 14, 10);
    malhas.forEach((malha) => {
      malha.geometry.computeBoundingBox();
      const t = malha.geometry.boundingBox.getSize(new THREE.Vector3());
      const base = Math.max(t.x, t.y, t.z) * 0.0075; // ~0,75% do tamanho do sachê
      const qtd = 260;
      const amostra = new MeshSurfaceSampler(malha).build();
      const gotas = new THREE.InstancedMesh(gota, agua, qtd);
      const pos = new THREE.Vector3(), nor = new THREE.Vector3(), alvo = new THREE.Vector3();
      const aux = new THREE.Object3D();
      for (let i = 0; i < qtd; i++) {
        amostra.sample(pos, nor);
        // tamanhos variados: muitas pequenas, poucas grandes
        const r = base * (0.35 + Math.pow(Math.random(), 2.2) * 1.6);
        aux.position.copy(pos).addScaledVector(nor, r * 0.12);
        aux.lookAt(alvo.copy(aux.position).add(nor));
        aux.scale.set(r, r * (0.8 + Math.random() * 0.35), r * 0.38); // achatada sobre o plástico
        aux.updateMatrix();
        gotas.setMatrixAt(i, aux.matrix);
      }
      malha.add(gotas);
    });
  }

  let atual = Number(secao?.dataset.saborAtual || 0);
  aplicar(await texturasDe(atual));
  // depois do primeiro, pré-carrega os outros sabores aos poucos
  sabores.forEach((_, i) => { if (i !== atual) setTimeout(() => texturasDe(i), 600 + i * 250); });

  palco.prepend(canvas);
  palco.classList.add('is-3d');

  const medir = () => {
    const r = palco.getBoundingClientRect();
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / Math.max(r.height, 1);
    camera.updateProjectionMatrix();
  };
  medir();
  new ResizeObserver(medir).observe(palco);

  // troca de sabor: uma volta inteira; a estampa troca quando o sachê está de lado
  let giroExtra = 0, troca = null;
  const trocar = async (i) => {
    if (i === atual) return;
    const tex = texturasDe(i);
    if (semMovimento) { aplicar(await tex); atual = i; return; }
    troca = { inicio: performance.now(), dur: 900, tex, i, aplicada: false };
  };
  secao?.addEventListener('lcw:sabor', (e) => trocar(e.detail.indice));

  let visivel = false, quadro = 0;
  const progresso = () => {
    const r = secao.getBoundingClientRect();
    return Math.min(Math.max((window.innerHeight - r.top) / (window.innerHeight + r.height), 0), 1);
  };
  const passo = (agora) => {
    quadro = 0;
    if (!visivel) return;
    if (troca) {
      const t = Math.min((agora - troca.inicio) / troca.dur, 1);
      giroExtra = suave(t) * Math.PI * 2;
      if (!troca.aplicada && t > 0.22) { // ~90°: de lado
        troca.aplicada = true;
        troca.tex.then((tx) => { aplicar(tx); atual = troca ? troca.i : atual; });
      }
      if (t >= 1) { troca = null; giroExtra = 0; }
    }
    // uma volta inteira enquanto a seção atravessa a tela; de frente no meio
    const rolagem = semMovimento ? 0 : (progresso() - 0.5) * Math.PI * 2;
    piv.rotation.y = rolagem + giroExtra;
    piv.position.y = semMovimento ? 0 : Math.sin(agora / 1400) * 0.003;
    inclinado.rotation.z = 0 + (semMovimento ? 0 : Math.sin(agora / 2100) * 0.04);
    renderer.render(cena, camera);
    quadro = requestAnimationFrame(passo);
  };
  new IntersectionObserver(([e]) => {
    visivel = e.isIntersecting;
    if (visivel && !quadro) quadro = requestAnimationFrame(passo);
  }).observe(palco);
};

const iniciar = () => {
  document.querySelectorAll('[data-sache-3d]').forEach((palco) => {
    // só monta quando a seção chega perto da tela
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      montar(palco).catch((erro) => {
        palco.classList.remove('is-3d');
        console.warn('Sachê 3D indisponível, mantendo a foto.', erro);
      });
    }, { rootMargin: '400px' });
    obs.observe(palco);
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
else iniciar();
document.addEventListener('shopify:section:load', iniciar);
